import type { ActivityHistoryEntry } from "./activityHistoryTypes";

export type ActivityDetailField = {
  label: string;
  value: string;
};

function hasText(value: unknown): value is string | number {
  if (typeof value === "number") return Number.isFinite(value);
  return typeof value === "string" && value.trim().length > 0;
}

function asText(value: unknown): string | null {
  if (!hasText(value)) return null;
  return String(value);
}

function push(
  fields: ActivityDetailField[],
  label: string,
  value: unknown,
  { allowEmpty = false }: { allowEmpty?: boolean } = {},
) {
  if (hasText(value)) {
    fields.push({ label, value: String(value) });
    return;
  }
  if (allowEmpty) {
    fields.push({ label, value: "—" });
  }
}

function isPageView(activity: ActivityHistoryEntry): boolean {
  return (
    activity.activityClass === "PageView" ||
    activity.eventType === "sa-page-viewed" ||
    activity.activityType === "page-view"
  );
}

function isSupportMode(activity: ActivityHistoryEntry): boolean {
  return (
    activity.activityClass === "SupportMode" ||
    activity.entityClass === "Support" ||
    Boolean(activity.eventType?.startsWith("impersonation-"))
  );
}

function isLifecycle(activity: ActivityHistoryEntry): boolean {
  return [
    "Created",
    "Edited",
    "Archived",
    "Restored",
    "PermanentDelete",
    "AccessPermissions",
  ].includes(String(activity.activityClass || ""));
}

function isPermanentDelete(activity: ActivityHistoryEntry): boolean {
  return (
    activity.activityClass === "PermanentDelete" ||
    activity.lifecycleOperation === "permanent-delete" ||
    Boolean(activity.eventType?.includes("hard-deleted")) ||
    Boolean(activity.eventType?.includes("purged"))
  );
}

function isEmployeeEvent(activity: ActivityHistoryEntry): boolean {
  return activity.entityClass === "Employee" || activity.entityType === "SaEmployee";
}

function supportCaseId(activity: ActivityHistoryEntry): string | null {
  const payload = activity.detailsPayload || {};
  return (
    asText(payload.supportTicketId) ||
    asText(payload.supportCaseId) ||
    null
  );
}

function sessionDuration(activity: ActivityHistoryEntry): string | null {
  const minutes = activity.detailsPayload?.sessionDurationMinutes;
  if (minutes == null || minutes === "") return null;
  return `${minutes} minutes`;
}

function entityLabel(activity: ActivityHistoryEntry): string | null {
  const parts = [
    activity.entityClass,
    activity.entityName,
    activity.entityId ? `(${activity.entityId})` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * Event-aware Activity Details fields.
 * Only include rows that apply to the event class and/or have material values.
 */
export function buildActivityDetailSections(activity: ActivityHistoryEntry): {
  actor: ActivityDetailField[];
  activityInfo: ActivityDetailField[];
  context: ActivityDetailField[];
  technical: ActivityDetailField[];
} {
  const actor: ActivityDetailField[] = [];
  const activityInfo: ActivityDetailField[] = [];
  const context: ActivityDetailField[] = [];
  const technical: ActivityDetailField[] = [];

  push(actor, "Name", activity.userName, { allowEmpty: true });
  push(actor, "Email", activity.userEmail);
  push(actor, "Employee #", activity.actorEmployeeId || activity.employeeId);
  if (!isPageView(activity)) {
    push(actor, "Auth0 Sub", activity.actorSub);
  }

  push(activityInfo, "Activity", activity.activity, { allowEmpty: true });
  push(activityInfo, "Event Type", activity.eventType);
  push(
    activityInfo,
    "Type",
    activity.activityClass || activity.activityType,
    { allowEmpty: true },
  );
  push(activityInfo, "Status", activity.status, { allowEmpty: true });
  push(activityInfo, "Timestamp", activity.timestamp || activity.eventTimestamp, {
    allowEmpty: true,
  });
  push(activityInfo, "Entity Type", activity.entityClass);

  if (isEmployeeEvent(activity)) {
    push(activityInfo, "Affected Employee", activity.affectedEmployeeId);
  }

  const pageUrl =
    activity.pageUrl || asText(activity.detailsPayload?.routePath) || null;
  if (isPageView(activity) || pageUrl) {
    push(context, "Page URL", pageUrl);
    push(context, "Page title", activity.detailsPayload?.pageTitle);
    push(context, "Module", activity.detailsPayload?.moduleName);
  }

  if (isLifecycle(activity) || isPermanentDelete(activity) || activity.entityClass === "Music") {
    push(context, "Entity", entityLabel(activity));
    push(context, "Customer #", activity.customerNumber);
    push(context, "Lifecycle operation", activity.lifecycleOperation);
    push(context, "Reason", activity.reason);
  }

  if (isSupportMode(activity)) {
    push(context, "Client / Entity", entityLabel(activity));
    push(context, "Customer #", activity.customerNumber);
    push(context, "Support Case ID", supportCaseId(activity));
    push(context, "Support Mode session ID", activity.impersonationTicketId);
    push(context, "Session Duration", sessionDuration(activity));
    push(context, "Reason", activity.reason);
    push(context, "Brand", activity.detailsPayload?.brandName || activity.detailsPayload?.brandId);
  }

  if (isPermanentDelete(activity)) {
    push(context, "Entity snapshot", entityLabel(activity));
    push(context, "Lifecycle operation", activity.lifecycleOperation || "permanent-delete");
    push(context, "Reason", activity.reason);
    push(
      context,
      "Preflight",
      activity.detailsPayload?.dependencyCheck
        ? JSON.stringify(activity.detailsPayload.dependencyCheck)
        : null,
    );
  }

  // Always-useful technical fields when present
  push(technical, "Correlation ID", activity.correlationId);
  push(technical, "IP Address", activity.ipAddress);
  push(technical, "User Agent", activity.userAgent);
  if (!isPageView(activity) && activity.actorSub) {
    push(technical, "Actor (raw)", activity.actorSub);
  }

  // Deduplicate by label within each section (first wins)
  const dedupe = (rows: ActivityDetailField[]) => {
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(row.label)) return false;
      seen.add(row.label);
      return true;
    });
  };

  return {
    actor: dedupe(actor),
    activityInfo: dedupe(activityInfo),
    context: dedupe(context),
    technical: dedupe(technical),
  };
}
